import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import Overlay from "../../../Componentes/Overlay";
import FormComp from "../../../Componentes/FormComp";
import formStyle from "../../../Componentes/FormComp/FormComp.module.css";
import Modal from "../../../Componentes/Utils/Modal";
import Formulario from "../../../Componentes/Utils/Formulario";
import MultifileUploader from "../../../Componentes/Utils/MultiFileUploader";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import {
    agoraBrasilia,
    getTimezoneBrasilia,
    getTimezoneNavegador,
} from "../../../Funcoes/FormatBrasiliaDatetime";
import { useModal } from "../../../contexts/ModalContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";

const logHorario = (etapa, dados) => {
    console.log(`[AtendimentoAmb][horario] ${etapa}`, dados);
};

const ModalTitle = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
`;

const FormSection = styled.div`
    width: 100%;
    text-align: left;
    position: relative;

    .${formStyle.formChild} p {
        margin: 15px 0 5px 0;
        padding-left: 2px;
        text-align: left;
    }
`;

const ActionsRow = styled.div`
    display: flex;
    gap: 6px;
    margin-top: 15px;
    margin-bottom: 8px;
`;

const ActionButton = styled.button`
    flex: 1;
    width: 100%;
    height: 40px;
    border: none;
    border-radius: 10px;
    outline: none;
    box-sizing: border-box;
    background-color: ${(props) => (props.$amarelo ? "#e0e000" : "#4caf50")};
    color: ${(props) => (props.$amarelo ? "#1f2937" : "#fff")};
    font-weight: bold;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover:not(:disabled) {
        background-color: ${(props) => (props.$amarelo ? "#ccc900" : "#45a049")};
    }

    &:disabled {
        background-color: #c1c1c1;
        color: #fff;
        cursor: not-allowed;
    }
`;

const ConfirmOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 12px;
`;

const ConfirmBox = styled.div`
    width: min(350px, 92%);
    background-color: #fff;
    border-radius: 5px;
    padding: 20px 16px 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ConfirmTitle = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    text-align: center;
    line-height: 1.4;
`;

const ConfirmActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 20px;
    width: 100%;
`;

const ConfirmButton = styled.button`
    flex: 1;
    height: 36px;
    border: none;
    border-radius: 5px;
    font-weight: 700;
    cursor: pointer;
    background-color: ${(props) => (props.$primary ? "#0b6e4f" : "#e5e7eb")};
    color: ${(props) => (props.$primary ? "#fff" : "#1f2937")};

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const TIPO_ATENDIMENTO_OPTIONS = ["INTERNO", "EXTERNO"];
const INTERNACAO_OPTIONS = ["NÃO", "SIM"];

const emptyColab = {
    nome: "",
    centro_custo: "",
    contrato: "",
    lider: "",
    matricula: "",
    funcao: "",
};

const ModalNovoAtendimento = ({ onSaved }) => {

    const api = useApi();
    const { closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();

    const [cpf, setCpf] = useState("");
    const [queixa, setQueixa] = useState("");
    const [tipoAtendimento, setTipoAtendimento] = useState("");
    const [internacao, setInternacao] = useState("");
    const [descricao, setDescricao] = useState("");
    const [solucao, setSolucao] = useState("");
    const [custos, setCustos] = useState("0.00");
    const [anexos, setAnexos] = useState([]);
    const [confirmAction, setConfirmAction] = useState(null);
    const [saving, setSaving] = useState(false);
    const [colab, setColab] = useState(emptyColab);
    const [queixaOptions, setQueixaOptions] = useState([]);

    const formRef = useRef(null);
    const dataInicioRef = useRef(null);
    const requestIdRef = useRef(0);

    const clearColab = useCallback(() => {
        setColab(emptyColab);
    }, []);

    useEffect(() => {
        const dataInicio = agoraBrasilia();
        dataInicioRef.current = dataInicio;

        logHorario("modal_aberto", {
            data_inicio_brasilia: dataInicio,
            timezone_oficial: getTimezoneBrasilia(),
            timezone_navegador: getTimezoneNavegador(),
            instante_utc: new Date().toISOString(),
        });
    }, []);

    useEffect(() => {
        const loadQueixas = async () => {
            try {
                const { data } = await api.get(
                    "/Painel/API/AtendimentoAmb/Queixas/List/index.php"
                );
                const lista = data?.data ?? data;
                if (!Array.isArray(lista)) {
                    setQueixaOptions([]);
                    return;
                }
                setQueixaOptions(lista.map((item) => item.descricao).filter(Boolean));
            } catch (err) {
                console.error("Erro ao carregar queixas:", err);
                setQueixaOptions([]);
            }
        };

        loadQueixas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const digits = cpf.replace(/\D/g, "");

        if (digits.length !== 11) {
            clearColab();
            return undefined;
        }

        const currentRequest = ++requestIdRef.current;

        const fetchColab = async () => {
            try {
                const { data } = await api.get("/Painel/API/AtendimentoAmb/ColabByCpf/index.php", {
                    params: { cpf: digits },
                });

                if (currentRequest !== requestIdRef.current) return;

                if (!data?.success) {
                    clearColab();
                    notifyError(data?.message || "Colaborador não encontrado.");
                    return;
                }

                setColab({
                    nome: data.nome ?? "",
                    centro_custo: data.centro_custo ?? "",
                    contrato: data.contrato ?? "",
                    lider: data.lider ?? "",
                    matricula: data.matricula ?? "",
                    funcao: data.funcao ?? "",
                });
            } catch (err) {
                if (currentRequest !== requestIdRef.current) return;
                clearColab();
                const msg = err?.response?.data?.message || "Erro ao buscar colaborador pelo CPF.";
                notifyError(msg);
            }
        };

        fetchColab();

        return () => {
            requestIdRef.current += 1;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cpf, clearColab]);

    const handleCpfChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
        setCpf(digits);
    };

    const handleTipoAtendimentoChange = (e) => {
        const value = e.target.value;
        setTipoAtendimento(value);
        if (value === "EXTERNO") {
            setInternacao("NÃO");
        } else {
            setInternacao("");
        }
    };

    const handleCustosChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "");
        const numero = parseInt(digits || "0", 10) / 100;
        setCustos(numero.toFixed(2));
    };

    const validateForm = useCallback(() => {
        const form = formRef.current;
        if (form && !form.checkValidity()) {
            form.reportValidity();
            return false;
        }
        if (!colab.matricula || !colab.nome) {
            notifyError("Informe um CPF válido para carregar o colaborador.");
            return false;
        }
        return true;
    }, [colab.matricula, colab.nome, notifyError]);

    const handleActionClick = (action) => (e) => {
        e.preventDefault();
        if (saving) return;
        if (!validateForm()) {
            notifyError("Preencha todos os campos.");
            return;
        }
        setConfirmAction(action);
    };

    const handleConfirmCancel = () => {
        if (saving) return;
        setConfirmAction(null);
    };

    const handleConfirm = async () => {
        if (!confirmAction || saving) return;

        const acao = confirmAction;
        const finalizar = acao === "finalizar";
        const dataInicio = dataInicioRef.current ?? agoraBrasilia();
        const dataFinal = finalizar ? agoraBrasilia() : null;

        logHorario("antes_salvar", {
            acao,
            data_inicio_brasilia: dataInicio,
            data_final_brasilia: dataFinal,
            timezone_oficial: getTimezoneBrasilia(),
            timezone_navegador: getTimezoneNavegador(),
            instante_utc: new Date().toISOString(),
        });

        setSaving(true);
        setConfirmAction(null);

        const formData = new FormData();
        formData.append("cpf", cpf);
        formData.append("nome", colab.nome);
        formData.append("matricula", colab.matricula);
        formData.append("funcao", colab.funcao ?? "");
        formData.append("centro_custo", colab.centro_custo ?? "");
        formData.append("contrato", colab.contrato ?? "");
        formData.append("lider", colab.lider ?? "");
        formData.append("queixa", queixa);
        formData.append("atendimento", tipoAtendimento);
        formData.append("descricao", descricao);
        formData.append("solucao", solucao);
        formData.append("custo", custos);
        formData.append("data_inicial", dataInicio);
        formData.append("data_final", finalizar ? dataFinal : "null");
        formData.append("acao", acao);
        formData.append("internacao", tipoAtendimento === "EXTERNO" ? internacao : "");

        anexos.forEach((item) => {
            if (item?.file) {
                formData.append("files[]", item.file);
            }
        });

        try {
            const { data } = await api.post("/Painel/API/AtendimentoAmb/Save/index.php", formData);

            logHorario("resposta_salvar", data);

            if (data?.debug) {
                logHorario("debug_servidor", data.debug);
            }

            if (data?.success || data?.STATUS === "SUCCESS") {
                notifySuccess(data?.message || "Atendimento salvo com sucesso.");
                onSaved?.();
                closeModal();
                return;
            }

            notifyError(data?.message || data?.ERROR || "Erro ao salvar atendimento.");
        } catch (err) {
            console.error(err);
            const msg = err?.response?.data?.message || "Erro ao salvar atendimento.";
            logHorario("erro_salvar", err?.response?.data ?? err.message);
            notifyError(msg);
        } finally {
            setSaving(false);
        }
    };

    const confirmTitle =
        confirmAction === "finalizar"
            ? "DESEJA FINALIZAR O ATENDIMENTO ?"
            : "DESEJA COLOCAR O ATENDIMENTO EM ANDAMENTO ?";

    return (
        <Overlay state>
            <Modal header close={closeModal} size="md">
                <ModalTitle>NOVO ATENDIMENTO</ModalTitle>

                <FormSection>
                <Formulario
                    ref={formRef}
                    onSubmit={(e) => e.preventDefault()}
                >
                    <FormComp
                        title="CPF"
                        type="text"
                        placeholder="000.000.000-00"
                        valor={formatCPF(cpf)}
                        changeFunc={handleCpfChange}
                        required
                    />
                    <FormComp
                        title="NOME"
                        type="text"
                        valor={colab.nome}
                        readOnly
                    />
                    <FormComp
                        title="CENTRO DE CUSTO"
                        type="text"
                        valor={colab.centro_custo}
                        readOnly
                    />
                    <FormComp
                        title="CONTRATO"
                        type="text"
                        valor={colab.contrato}
                        readOnly
                    />
                    <FormComp
                        title="LÍDER"
                        type="text"
                        valor={colab.lider}
                        readOnly
                    />
                    <FormComp
                        title="QUEIXA / OCORRÊNCIA"
                        type="select"
                        options={queixaOptions}
                        valor={queixa}
                        changeFunc={(e) => setQueixa(e.target.value)}
                        required
                    />
                    <FormComp
                        title="TIPO ATENDIMENTO"
                        type="select"
                        options={TIPO_ATENDIMENTO_OPTIONS}
                        valor={tipoAtendimento}
                        changeFunc={handleTipoAtendimentoChange}
                        required
                    />
                    {tipoAtendimento === "EXTERNO" && (
                        <FormComp
                            title="INTERNAÇÃO"
                            type="select"
                            options={INTERNACAO_OPTIONS}
                            valor={internacao}
                            changeFunc={(e) => setInternacao(e.target.value)}
                            required
                        />
                    )}
                    <FormComp
                        title="DESCRIÇÃO"
                        type="textarea"
                        valor={descricao}
                        changeFunc={(e) => setDescricao(e.target.value)}
                        required
                    />
                    <FormComp
                        title="SOLUÇÃO"
                        type="textarea"
                        valor={solucao}
                        changeFunc={(e) => setSolucao(e.target.value)}
                        required
                    />
                    <FormComp
                        title="CUSTOS"
                        type="text"
                        placeholder="0.00"
                        valor={custos}
                        changeFunc={handleCustosChange}
                        required
                    />
                    <MultifileUploader files={anexos} setFiles={setAnexos} />
                    <ActionsRow>
                        <ActionButton
                            type="button"
                            $amarelo
                            onClick={handleActionClick("aguardar")}
                            disabled={saving}
                        >
                            EM ANDAMENTO
                        </ActionButton>
                        <ActionButton
                            type="button"
                            onClick={handleActionClick("finalizar")}
                            disabled={saving}
                        >
                            FINALIZAR
                        </ActionButton>
                    </ActionsRow>
                </Formulario>

                {confirmAction && (
                    <ConfirmOverlay>
                        <ConfirmBox>
                            <ConfirmTitle>{confirmTitle}</ConfirmTitle>
                            <ConfirmActions>
                                <ConfirmButton
                                    type="button"
                                    onClick={handleConfirmCancel}
                                    disabled={saving}
                                >
                                    NÃO
                                </ConfirmButton>
                                <ConfirmButton
                                    type="button"
                                    $primary
                                    onClick={handleConfirm}
                                    disabled={saving}
                                >
                                    {saving ? "SALVANDO…" : "SIM"}
                                </ConfirmButton>
                            </ConfirmActions>
                        </ConfirmBox>
                    </ConfirmOverlay>
                )}
                </FormSection>
            </Modal>
        </Overlay>
    );
};

export default ModalNovoAtendimento;
